/**
 * [화면] 홈 (`/`)
 * 로그인 여부에 따라 랜딩(비로그인)과 학생 홈(로그인)을 분기 렌더한다.
 *
 * 접근: 누구나(공개). 선생님 역할이면 `/teacher`로 강제 이동한다.
 * 데이터: 비로그인이면 API 호출 없음. 로그인이면 `StudentHome` 내부에서 fetch.
 *
 * 두 화면의 훅이 섞이지 않도록 각각 별도 컴포넌트로 두고 여기서는 분기만 한다.
 * 비로그인 방문자에게 인증이 필요한 fetch가 돌지 않게 하기 위함이다.
 *
 * ★ 랜딩은 정적 빌드 HTML 에 그대로 들어간다(SEO/GEO). 로그인 여부는 sessionStorage 라 마운트 뒤에만 알 수 있으므로
 * 서버/빌드 시점 기본은 "비로그인 = 랜딩"이고, 로그인 학생은 마운트 직후 StudentHome 으로 바뀐다(짧은 랜딩 노출 허용).
 * 이전처럼 마운트 전 null 을 돌려주면 JS 를 실행하지 않는 크롤러(네이버·AI 답변 엔진)가 빈 문서를 받는다.
 */
import React, { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import StudentHome from "@/components/home/StudentHome";

// 랜딩 번들은 청크로 분리하되(로그인 학생이 매번 내려받지 않게) 빌드 시점 HTML 에는 포함되도록 ssr 을 켠다.
// `ssr: false` 로 두면 프리렌더 결과에서 랜딩 본문이 통째로 빠져 검색엔진·AI 크롤러가 빈 페이지를 본다.
const LandingPage = dynamic(() => import("@/components/landing/LandingPage"));

function HomePage() {
  const [clientReady, setClientReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // ★ 교사 차단 가드. 선생님의 메인은 /teacher(학생 챕터 할당 현황)뿐이고
  // 학생 홈은 절대 금지. Amplify rewrite 누락 등으로 교사 딥링크 URL에
  // 이 셸이 서빙되는 경우까지 방어한다. URL이 /teacher/* 면 그 딥링크를
  // 클라이언트 라우팅으로 복원하고, 그 외에는 /teacher로 보낸다.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("role") !== "TEACHER") return;
      const { pathname, search } = window.location;
      // ★ router.replace가 아니라 브라우저 강제 이동. 학생 홈의 인증 API 401 →
      // 토큰 갱신 → window.location.reload()(axiosInstance)가 클라이언트 라우팅을
      // 취소시키는 경합이 있어, 마운트 즉시 하드 이동으로 확실히 이탈한다.
      window.location.replace(
        pathname.startsWith("/teacher") ? `${pathname}${search}` : "/teacher",
      );
    } catch {
      /* 저장소 접근 불가 환경 무시 */
    }
  }, []);

  // 정적 export라 sessionStorage는 마운트 이후에만 읽을 수 있다. 그 전까지는 랜딩(프리렌더와 동일)을 그대로 둔다.
  useEffect(() => {
    setIsLoggedIn(sessionStorage.getItem("accessToken") !== null);
    setClientReady(true);
  }, []);

  return clientReady && isLoggedIn ? <StudentHome /> : <LandingPage />;
}

export default HomePage;
